/**
 * Progression Scaling Configuration
 *
 * SINGLE SOURCE OF TRUTH for balance values shared by the game and the
 * headless simulator (tools/simulation/game-simulator.js). The simulator
 * defaults to these values; tools/simulation/simulation-config.json is
 * pure override/experiment space. To ship a validated experiment value,
 * edit it HERE and run `npm run build` (regenerates js/constants.js).
 *
 * Values are tuned so that:
 * - All 15 faction epic tech gear items are craftable in a 30-day cycle
 * - Inventory quantities stay in balanced range (50-150 per item)
 * - Higher-rarity intermediates are properly gated by their availability
 */

export const progressionScaling = {
  /**
   * Durable-gear craft cap. Skill (tech) gear and artifacts are equip-once
   * items - one slot per skill / per artifact slot, never consumed, sold, or
   * traded - so a second copy earns nothing (marks are already distinct-only)
   * and just burns materials. When false (default) each such design can be
   * crafted once per character; the crafting card shows "✓ CRAFTED" instead.
   * Flip to true to re-enable duplicate crafting (e.g. if a gear marketplace
   * ever makes spare copies worth something). Consumables are unaffected.
   */
  allowDuplicateCrafts: false,

  recipeInputs: {
    // Re-tuned for weighted per-item drops: selection weights spread drops across
    // all items (nothing starves to zero anymore), so demand carries the pacing
    common: 5, // Common ingredients cost 5x base (sized to rarity-filtered event loot income)
    uncommon: 5, // Uncommon ingredients cost 5x base
    rare: 4, // Rare ingredients cost 4x base
    epic: 4, // Epic ingredients cost 4x base (scarce)
    legendary: 1, // Legendary ingredients cost 1x base (very scarce)
    unique: 1, // Unique ingredients cost 1x base (very scarce)
  },

  /**
   * Skill points awarded per level up (character-core.js levelUpCore).
   * Formula: base + Math.floor(level / levelDivisor) * increment
   * where `level` is the level just REACHED.
   *
   * With the values below (base: 2, levelDivisor: 2, increment: 3):
   * - Level 1:   3 skill points (the starting allotment, not a level-up grant)
   * - Level 2-3: 5 per level-up
   * - Level 4-5: 8 per level-up
   * - Level 6-7: 11 per level-up
   */
  skillPointsPerLevel: {
    base: 2, // Base skill points awarded on level up
    levelDivisor: 2, // Award +increment skill points every N levels
    increment: 3, // Amount to increase per tier
  },

  /**
   * Per-skill synergy unlocks (progressive disclosure of the synergy layer):
   * hidden entirely below minLevel, then each skill's synergies are bought
   * with skill points. A synergy variant can fire only when it touches an
   * unlocked skill on either side (the event's skill, or the skill of the
   * player's partner device).
   */
  synergyUnlocks: {
    minLevel: 4, // Character level where the synergy layer is revealed
    cost: 2, // Skill points per skill unlock
  },

  /**
   * Raid XP tier weighting (Command era). The level curve's old assumption
   * ("level 8 in 1-2 months") predates promotion cycling; with 3-5 day
   * tours, character level was the binding progression constraint - tours
   * stalled at level 4-5 on tier-1 XP. Pushing tiers is now the leveling
   * ladder: T1 pacing (and first-promotion timing) unchanged, T2/T3 raids
   * pay multiples so deep tours can level past the plateau.
   */
  raidXpTierMultipliers: { 1: 1, 2: 3, 3: 5 },

  /**
   * Skill event damage scaling per raid tier
   * Damage formula: minDamage + random(0, maxDamage - minDamage)
   * where minDamage = baseDamage * tier, maxDamage = minDamage + (baseDamage * tier)
   *
   * Example with defaults (baseDamage: 5):
   * - Tier 1: 5-15 damage (5 + 0-10)
   * - Tier 2: 10-30 damage (10 + 0-20)
   * - Tier 3: 15-45 damage (15 + 0-30)
   */
  skillEventDamage: {
    baseDamage: 5, // Base damage multiplied by tier number (used if perTier not set)
    perTier: {
      1: { min: 20, max: 30 },
      2: { min: 25, max: 35 },
      3: { min: 30, max: 40 },
    },
  },

  /**
   * Bonus event cascade chance multipliers, per raid tier AND per cascade step.
   * Applied as: rarityDropChance * bonusEventCascadeMultipliers[tier][nextRarity]
   * when the end-of-room bonus event rolls to cascade common→rare→epic→legendary→unique.
   *
   * Per-rarity so the legendary step can be boosted (its base chance is near zero,
   * this is the main legendary blueprint source) WITHOUT saturating the rare/epic
   * steps - a flat multiplier clamped those to 100% per clean room.
   */
  bonusEventCascadeMultipliers: {
    // rare < 1.0 keeps leaf-rare cascade volume near recipe demand.
    // Legendary (= artifact blueprints, the whole legendary pool) is a TIER 3
    // EXCLUSIVE: reaching T3 is the plannable path to artifacts (and the
    // Syndicate boundary), not a lottery ticket at every tier. 50 saturates
    // the legendary step (0.02 base x 50 = chance 1.0 once the cascade
    // reaches epic), so a clean T3 room drops a blueprint at its epic-chain
    // rate (~3% hazard, ~7% gate).
    1: { rare: 0.5, epic: 1.0, legendary: 0, unique: 1.0 },
    2: { rare: 0.5, epic: 1.0, legendary: 0, unique: 1.0 },
    3: { rare: 0.5, epic: 1.0, legendary: 50, unique: 1.0 },
  },

  /**
   * Share of a promoted character's MATERIALS that passes to their successor.
   * The remainder is consigned to the Syndicate dock as Operations cargo
   * (command-core.js disburseInventory).
   *
   * Fixed, and was a three-rank Command upgrade ("Guild Vault", 80/240/720
   * marks) until the S7c pass removed it. The rank bought nothing: gear crafted
   * per tour is FLAT across a 2.5x range of this value - 5.57 at 10/20/30,
   * 6.07 at 15/30/50, 5.61 at 25/50/75, n=4 each - so the upgrade taxed 1,040
   * marks for an effect that does not exist. An upgrade that cannot be measured
   * is worse than no upgrade, because buying it is a trap.
   *
   * Why the share does not matter: it is not a one-way transfer. Whatever the
   * successor inherits and does not CONSUME is disbursed again at their own
   * promotion, and again after that, so the dial changes timing rather than
   * totals. Measured: at 75% carryover the L3 dock was unchanged (17,977 vs
   * 18,504 units) and zero convoys were ever refused for cargo.
   *
   * 0.3 because it equals the old rank-3 value - nobody who had fully invested
   * is worse off - and because a flat "a third of your kit passes on" reads
   * cleanly in the fiction. Any value in 0.1-0.75 measures the same.
   */
  vaultCarryover: 0.3,

  /**
   * Share of the NON-carried remainder that reaches the Syndicate dock as
   * Operations cargo. The rest is consumed — spent living, in the fiction.
   *
   * The accounting used to be closed: 30% to the successor, 70% to L3, nothing
   * lost. That was tidy and produced an unusable economy. A dock held ~18,000
   * units while a whole era's convoys consumed ~1,500 (8%), so the "common
   * material piles plateau (<300/signature)" target measured ~2,500 and rising,
   * and cargo could never be a real constraint on Operations — even 10x the
   * cargo per convoy would not have drained it.
   *
   * No dial inside Operations could fix that, because the problem is supply,
   * not demand. This is the supply valve.
   */
  consignmentRate: 0.15,

  /**
   * Command layer (M2) - Command Marks formula coefficients + unlock threshold.
   * Consumed by command-core.js calculateCommandMarks:
   *   marks = perTechGear       * distinct tech gear owned (inventory + equipped)
   *         + perArtifactUnlock * lifetime.artifactsUnlocked
   *         + perArtifactCraft  * lifetime.artifactsCrafted
   *         + floor(lifetime.t3Wins / t3WinsDivisor)
   *         + perLevel          * level
   * The sim promotion policy tunes these so an un-upgraded character #1's
   * marks/hour peaks ~day 3-5 (see docs/Roadmap-070426.md playtime budget).
   */
  commandMarks: {
    // Marks reward CRAFTING, which is the player's active progress: skill gear
    // early (the only thing craftable at first), artifacts later (once T3 is
    // reachable). Character level is an ENABLER, not a payout - it unlocks
    // higher tiers, which yield T3 wins + blueprints -> artifacts; its direct
    // marks contribution is deliberately small so it can't become the main
    // driver (it previously grew to ~50% of marks as accounts matured).
    // Held at 4. Raising it was MEASURED in the S7c pass and rejected: at 8,
    // three runs put the first founding on day 31/34/31 - identical to the
    // 31-36 baseline - while Writs overshot to 34-38 and the L1 era collapsed
    // from ~40h to 18-21h. Two reasons, both structural:
    //
    //  - It cannot shorten a tour. Tour length is when cumulative marks/hour
    //    PEAKS, and a bigger gear channel moves that peak later by about as
    //    much as it pays, so the schedule does not move.
    //  - Marks are also the CURRENCY for Command upgrades, so doubling this
    //    halves the real price of the whole catalogue. The first promotion
    //    fires at the 60-mark floor, which needs ~15 gear at 4 and only 8 at
    //    8 - halving the onboarding era as a side effect.
    //
    // The L2 era overruns because tours 2-15 run ~26h against the ~18h the
    // 300h budget implies. That is a CYCLE COMPRESSION problem (officer-academy
    // / guild-vault head starts not paying off enough), not a throughput one,
    // and this coefficient is the wrong lever for it.
    perTechGear: 4,
    // Artifacts split into two channels (see command-core unlockArtifactCore):
    //   perArtifactUnlock - ONE-TIME per design per ACCOUNT, banked when the
    //     design's blueprint is consumed to unlock it. The milestone: 27
    //     designs => a finite ~675-mark collection goal.
    //   perArtifactCraft - each distinct craft per character (parallel to gear,
    //     ~2x a gear piece). Keeps a modest sustained artifact-marks trickle
    //     without the runaway 25-per-craft farm.
    perArtifactUnlock: 25,
    perArtifactCraft: 8,
    // T3 wins are the "operating at high tier" marks channel that level
    // enables (2 wins per mark); artifacts are the high-tier crafting payoff
    t3WinsDivisor: 2,
    perLevel: 1,
    // Command tab reveals when estimated marks first reach this. Tied to
    // perTechGear: it fires on the 7th piece of gear. If perTechGear ever
    // moves, scale this by the same ratio - it is a progression milestone
    // measured in what the player has BUILT, and leaving it fixed would slide
    // a designed reveal earlier as a side effect of an unrelated change.
    unlockThreshold: 25,
  },

  /**
   * Promotion credit levy (applied in command-core.js promoteCharacterCore).
   * Credits are account-shared, so promotion takes a percentage cut instead
   * of a flat reset: kept = max(floor, credits * keepFraction). The levy is
   * the economy's terminal credit sink — it must scale with wealth to keep
   * absorbing late-game surplus; the floor doubles as a safety net so a
   * broke account still restarts with working capital.
   */
  promotionCreditLevy: {
    keepFraction: 0.25,
    floor: 1000,
  },

  /**
   * Collapse Key recovery — the ONLY tunable in the story track.
   *
   * Chance that a successful raid recovers the next Collapse Key its faction is
   * still missing. Not an economic number: keys have no value, no sink and no
   * sale, so this tunes STORY PACING alone and no balance target reads it.
   *
   * The tier table (keys 1-2 at T1, 3-4 at T2, 5-7 at T3) is what actually
   * paces the arc — a T1 character can only ever hold two keys, however long
   * they raid. This number decides how quickly they reach that ceiling: at 0.25
   * the first key lands in the opening session and the T1 pair inside ~8 wins,
   * which is the point of shipping keys 1-7 at all. The game previously told
   * the player nothing until ~900 hours in.
   */
  collapseKeyDropChance: 0.25,

  /**
   * Raid credit reward scaling (applied in raidUtil.js applyCreditsScaling)
   * effective = earned * globalMultiplier * tierMultipliers[tier]
   */
  creditsScaling: {
    globalMultiplier: 0.3,
    tierMultipliers: {
      1: 1.0,
      2: 1.0,
      3: 1.0,
    },
  },

  /**
   * Energy repurchase escalation (applied in raidUtil.js
   * getConsumablePurchasePrice): energy-restoring consumables cost
   * base * escalationFactor^(purchases today), resetting daily.
   * The primary Command-era credit sink - converts surplus credits into
   * raid throughput at a net credit loss (late game: 5-6 packs/day
   * ~ the 4-10k/day income it must soak). 1.0 disables escalation.
   */
  energyPurchaseScaling: {
    escalationFactor: 2.0,
  },

  /**
   * Per-tour crafting discount (Command layer). Each completed tour makes
   * crafting cheaper for every later tour — the institutional-knowledge payoff.
   *
   * DERIVED, never compounded:
   *   globalMultiplier = craftingCostScaling.globalMultiplier * factor^(tour - 1)
   *
   * It used to be applied by multiplying raidUtil's module state at each
   * promotion, which main.js then re-seeded from config on every boot — so the
   * discount silently vanished on reload. Deriving it from `command.tour` makes
   * it reload-safe, and makes L3's founding reset free: tour returns to 1, so
   * the multiplier returns to base with nothing extra to persist.
   *
   * 1.0 disables the discount.
   */
  craftingDiscountPerTour: 0.9,

  /**
   * Crafting credit cost scaling (applied in raidUtil.js applyCraftingCostScaling)
   * effective = cost * globalMultiplier * rarityMultipliers[rarity]
   */
  craftingCostScaling: {
    globalMultiplier: 5,
    rarityMultipliers: {
      common: 1.0,
      uncommon: 1.0,
      rare: 1.0,
      epic: 1.0,
      legendary: 1.0,
      unique: 1.0,
      artifact: 1.0,
    },
  },
};

/**
 * Get the input multiplier for a given rarity (per-input scaling)
 * @param {string} rarity - The rarity of the input item ('common', 'uncommon', 'rare', 'epic', etc.)
 * @returns {number} Multiplier for this input's quantity
 */
export function getInputRarityMultiplier(rarity) {
  const multiplier = progressionScaling.recipeInputs[rarity];
  if (multiplier === undefined) {
    console.warn(`Unknown input rarity: ${rarity}, returning 1.0`);
    return 1.0;
  }
  return multiplier;
}

/**
 * Round a quantity to a clean multiple (1, 10, or 100) based on magnitude
 *
 * Logic:
 * - 1-9: round to nearest 1
 * - 10-99: round to nearest 10
 * - 100-999: round to nearest 100
 * - 1000+: round to nearest 100
 *
 * Always rounds UP to ensure we don't accidentally make recipes too easy.
 *
 * @param {number} quantity - The quantity to round
 * @returns {number} Rounded quantity
 */
export function roundQuantityToCleanMultiple(quantity) {
  if (quantity < 10) {
    // Keep 1-9 as-is
    return Math.max(1, Math.ceil(quantity));
  } else if (quantity < 100) {
    // Round 10-99 to nearest 5
    return Math.ceil(quantity / 5) * 5;
  } else {
    // Round 100+ to nearest 10
    return Math.ceil(quantity / 10) * 10;
  }
}

/**
 * Scale a recipe's inputs by the per-rarity input multipliers
 *
 * Each ingredient is multiplied by its rarity's configured multiplier.
 * These multipliers come from simulator balancing and ensure that:
 * - All 15 tech gear items are craftable
 * - Resources are properly gated by their availability
 *
 * @param {Array} inputs - Array of { id, qty, rarity } input objects
 * @param {Function} getRarityFn - Function to get rarity of an item (id => rarity)
 * @returns {Array} Scaled inputs with clean rounded quantities
 */
export function scaleRecipeInputs(inputs, getRarityFn = null) {
  if (!inputs || !Array.isArray(inputs)) {
    return inputs;
  }

  return inputs.map((input) => {
    let inputQty = input.qty;

    // Get input rarity (from input object or from provided function)
    let inputRarity = input.rarity;
    if (!inputRarity && getRarityFn) {
      inputRarity = getRarityFn(input.id);
    }

    // Apply per-input scaling based on rarity
    if (inputRarity) {
      const inputMultiplier = getInputRarityMultiplier(inputRarity);
      inputQty *= inputMultiplier;
    }

    return {
      ...input,
      qty: roundQuantityToCleanMultiple(inputQty),
    };
  });
}
